import React, { useState, useEffect } from 'react';
import { API_BASE_URL, enhancedFetch } from '../services/api';

const Profile = ({ token, onLogout, onUnauthorized, setError, setSuccess }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setErrorFlg] = useState(null);
  const [success, setSuccessFlg] = useState(null);
  

  const handleError = (error) => {
    setError(error);
    setErrorFlg(error);
  };

  const handleSuccess = (message) => {
    setSuccess(message);
    setSuccessFlg(message);
  };

  // -profile update
  const [email, setEmail] = useState('');
  
  // 邮箱更新表单
  const [emailForm, setEmailForm] = useState({
    email: ''
  });
  
  // 密码更新表单
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // MAF状态
  const [mafSetup, setMafSetup] = useState({
    secret: '',
    qrCode: '',
    uri: ''
  });
  const [mafCode, setMafCode] = useState('');
  const [showMafSetup, setShowMafSetup] = useState(false);
  
  // 获取用户信息
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await enhancedFetch(`${API_BASE_URL}/profile/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }, onUnauthorized);
        
        if (!response.ok) {
          throw new Error('获取用户信息失败');
        }
        
        const data = await response.json();
        setProfile(data);
        setEmailForm({ email: data.email });
        setEmail(data.email);
      } catch (err) {
        handleError('加载用户信息失败: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [token, onUnauthorized]);
  
  // 显示成功消息后自动清除
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        handleSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  // 显示错误消息后自动清除
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        handleError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  // 更新邮箱
  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: emailForm.email })
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '更新邮箱失败');
      }
      
      const data = await response.json();
      setProfile(data.user);
      setEmail(data.user.email);
      handleSuccess('邮箱更新成功');
    } catch (err) {
      handleError(err.message);
    }
  };
  
  // 更新密码
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    // 验证密码
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      handleError('新密码不匹配');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      handleError('密码长度至少6位');
      return;
    }
    
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/profile/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        })
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '更新密码失败');
      }
      
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      handleSuccess('密码更新成功');
    } catch (err) {
      handleError(err.message);
    }
  };
  
  // 设置MAF
  const handleSetupMAF = async () => {
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/profile/maf/setup`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '设置MAF失败');
      }
      
      const data = await response.json();
      setMafSetup(data);
      setShowMafSetup(true);
    } catch (err) {
      handleError(err.message);
    }
  };
  
  // 保存MAF密钥
  const handleSaveMAFSecret = async () => {
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/profile/maf/save-secret`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ secret: mafSetup.secret })
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '保存MAF密钥失败');
      }
      
      await response.json();
    } catch (err) {
      handleError(err.message);
    }
  };
  
  // 启用MAF
  const handleEnableMAF = async (e) => {
    e.preventDefault();
    
    try {
      // 先保存密钥
      await handleSaveMAFSecret();
      
      const response = await enhancedFetch(`${API_BASE_URL}/profile/maf/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: mafCode })
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '启用MAF失败');
      }
      
      await response.json();
      setProfile({ ...profile, maf_enabled: true });
      setMafCode('');
      setShowMafSetup(false);
      handleSuccess('MAF认证启用成功');
    } catch (err) {
      handleError(err.message);
    }
  };
  
  // 禁用MAF
  const handleDisableMAF = async (e) => {
    e.preventDefault();
    
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/profile/maf/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: mafCode })
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '禁用MAF失败');
      }
      
      await response.json();
      setProfile({ ...profile, maf_enabled: false, maf_secret: null });
      setMafCode('');
      handleSuccess('MAF认证禁用成功');
    } catch (err) {
      handleError(err.message);
    }
  };
  
  if (loading) {
    return (
      <div className="card">
        <div className="card">
          <div className="loading-placeholder">
            <p>正在加载用户信息...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!profile) {
    return <div className="error">无法加载用户信息</div>;
  }
  
  return (
    <div className="card">
      <div className="card-header">
        <h2>个人资料设置</h2>
        <p>管理您的账户设置和安全</p>
      </div>
      
      <div className="profile-content">
        {/* 用户基本信息 */}
        <div className="profile-section card">
          <h3>账户信息</h3>
          <div className="profile-info-grid">
            <div className="info-item">
              <span className="info-label">用户名:</span>
              <span className="info-value">{profile?.username}</span>
            </div>
            <div className="info-item">
              <span className="info-label">用户ID:</span>
              <span className="info-value">{profile?.id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">邮箱:</span>
              <span className="info-value">{profile?.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">管理员:</span>
              <span className="info-value">{profile?.is_superuser ? '是' : '否'}</span>
            </div>
          </div>
        </div>
        
        {/* 邮箱更新 */}
        <div className="profile-section card">
          <h3>更新邮箱</h3>
          <form onSubmit={handleEmailUpdate} className="profile-form">
            <div className="form-group">
              <label htmlFor="email">邮箱地址:</label>
              <input
                type="email"
                id="email"
                value={emailForm.email}
                onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                required
                placeholder="输入新邮箱地址"
              />
            </div>
            <button type="submit" className="primary-button">更新邮箱</button>
          </form>
        </div>
        
        {/* 密码更新 */}
        <div className="profile-section card">
          <h3>更改密码</h3>
          <form onSubmit={handlePasswordUpdate} className="profile-form">
            <div className="form-group">
              <label htmlFor="currentPassword">当前密码:</label>
              <input
                type="password"
                id="currentPassword"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                required
                placeholder="输入当前密码"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">新密码:</label>
              <input
                type="password"
                id="newPassword"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
                minLength="6"
                placeholder="输入新密码"
              />
              <div className="help-text">密码长度至少6位</div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">确认新密码:</label>
              <input
                type="password"
                id="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                required
                placeholder="再次输入新密码"
              />
            </div>
            <button type="submit" className="primary-button">更改密码</button>
          </form>
        </div>
        
        {/* MAF二次认证 */}
        <div className="profile-section card">
          <h3>多重身份验证 (MAF)</h3>
          {profile?.maf_enabled ? (
          <div className="maf-enabled">
              <div className="status-badge status-success">MAF已启用</div>
              <form onSubmit={handleDisableMAF} className="profile-form">
                <div className="form-group">
                  <label htmlFor="disableMafCode">输入MAF验证码以禁用:</label>
                  <input
                    type="text"
                    id="disableMafCode"
                    value={mafCode}
                    onChange={(e) => setMafCode(e.target.value)}
                    placeholder="输入6位验证码"
                    maxLength="6"
                    required
                  />
          </div>
                <button type="submit" className="danger-button">禁用MAF</button>
              </form>
            </div>
          ) : (
            <div className="maf-disabled">
              <div className="status-badge status-warning">MAF未启用</div>
              {showMafSetup ? (
                <div className="maf-setup">
                  <p>使用您的身份验证器应用扫描二维码:</p>
                  <div className="qr-code-container">
                    <img src={mafSetup.qrCode} alt="MAF二维码" className="qr-code" />
                  </div>
                  <p>或手动输入此密钥: <strong>{mafSetup.secret}</strong></p>
                  <form onSubmit={handleEnableMAF} className="profile-form">
              <div className="form-group">
                      <label htmlFor="enableMafCode">输入MAF验证码:</label>
                <input
                  type="text"
                        id="enableMafCode"
                  value={mafCode}
                  onChange={(e) => setMafCode(e.target.value)}
                        placeholder="输入6位验证码"
                        maxLength="6"
                  required
                />
              </div>
              <button type="submit" className="primary-button">启用MAF</button>
                  </form>
                  <button onClick={() => setShowMafSetup(false)} className="link-button">
                取消
              </button>
          </div>
        ) : (
                <button onClick={handleSetupMAF} className="primary-button">设置MAF</button>
              )}
          </div>
        )}
      </div>
      
        {/* 登出按钮 */}
        <div className="profile-section card">
          <h3>账户操作</h3>
          <button onClick={onLogout} className="logout-button">退出登录</button>
        </div>
      </div>
    </div>
  );
};

export default Profile;